<template>
  <div class="container-fluid compu-minutes-editor">
    <page-header
      :page="pageConfig"
      :actions="pageActions"
      left-side-class="col-12 col-lg-8"
      right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
    ></page-header>

    <filter-manager
      v-model="filter.model"
      :filters="filtersHeader"
      class="filter-manager_extended"
      search-container-class="search-btn"
      :display-advanced="false"
      @search="() => onSearchHandler()"
    />

    <custom-separator text="Resultados" class="my-4 custom-separator-line" />

    <div v-if="hasResults" class="compu-minutes-editor__results">
      <result-row
        v-for="item in results"
        :key="item.id"
        :item="item"
        :can-return-to-draft="canReturnToDraft"
        @edit="goToMinute"
        @print="handlePrintMinute"
        @return-to-draft="handleReturnToDraft"
        @discard-draft="handleDiscardDraft"
      />

      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onSearchHandler"
      />
    </div>

    <custom-result-not-found
      v-else-if="!isLoading"
      class="mt-4 row pt-3"
      text="No hay información disponible."
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow.vue';
import compuMinuteService from '@/modules/compu-minutes-editor/api';
import documentsService from '@/services/documents';
import documentService from '@/services/pei/general';
import academicService from '@/services/api/academicUtils.api.js';
import compuMinuteStatus from '@/utils/constants/compuMinuteStatus.js';
import alertMixin from '@/mixin/alert.mixin';

const _DefaultPageSize = 10;
const _MinAcademicYear = 2025;
const _ReturnToDraftPermission = 'pei.compu-minutes-editor.returntodraft';

export default {
  name: 'CompuMinutesEditorMain',
  mixins: [alertMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
  },
  data() {
    return {
      isLoading: false,
      results: [],
      filter: {
        model: {
          academicYear: null,
          status: null,
          searchText: '',
        },
        options: {
          academicYears: [],
          statuses: [],
        },
      },
      pagination: {
        count: 0,
        currentPage: 1,
        pageCount: 1,
        totalItemCount: 0,
        pageSize: _DefaultPageSize,
      },
    };
  },
  computed: {
    pageConfig() {
      return {
        icon: 'fas fa-file-signature',
        title: 'Editor de Minutas de COMPU',
        routes: ['Inicio', 'PEI', 'Editor de Minutas de COMPU'],
      };
    },
    pageActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          visible: true,
          click: () => {
            this.$router.push('/pei');
          },
        },
        {
          text: 'Crear Minuta',
          icon: 'fas fa-file-plus',
          variant: 'success',
          visible: true,
          click: () => {
            this.$router.push({ name: 'compu-minutes-editor-create' });
          },
        },
      ];
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filter.options.academicYears,
          value: this.filter.model.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4 ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4 ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'searchText',
          component: 'CustomInput',
          value: this.filter.model.searchText,
          placeholder: 'Estudiante, SIE o Número de Minuta',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          type: 'text',
          class: 'col-12 col-md-4 ml-0 mr-0',
        },
      ];
    },
    hasResults() {
      return this.results.length > 0;
    },
    canReturnToDraft() {
      return this.userHasPermissions(_ReturnToDraftPermission);
    },
  },
  async mounted() {
    await Promise.all([this.loadAcademicYears(), this.loadStatuses()]);
    await this.onSearchHandler();
  },
  methods: {
    async loadAcademicYears() {
      const [academicYears, currentAcademicYearResponse] = await Promise.all([
        academicService.getHRrequestAcademicYears(_MinAcademicYear),
        academicService.getCurrentAcademicYear(),
      ]);

      const currentAcademicYear = this.normalizeAcademicYear(
        currentAcademicYearResponse.data
      );
      const options = (academicYears || []).map(this.normalizeAcademicYear);
      const hasCurrentAcademicYear = options.some(
        (academicYear) => academicYear.id === currentAcademicYear.id
      );

      this.filter.options.academicYears = hasCurrentAcademicYear
        ? options
        : [currentAcademicYear, ...options];

      this.filter.model.academicYear =
        this.filter.options.academicYears.find(
          (academicYear) => academicYear.id === currentAcademicYear.id
        ) ||
        this.filter.options.academicYears[0] ||
        null;
    },
    async loadStatuses() {
      const statuses = await compuMinuteService.fetchStatuses();

      this.filter.options.statuses = statuses.filter(
        (status) => status.name && status.name !== '-'
      );
    },
    normalizeAcademicYear(academicYear) {
      const source = academicYear || {};

      return {
        ...source,
        name:
          source.name ||
          source.description ||
          source.academicYear ||
          source.year ||
          '-',
      };
    },
    async onSearchHandler(currentPage = 1) {
      this.isLoading = true;

      try {
        const { academicYear, status, searchText } = this.filter.model;
        const { data } = await compuMinuteService.search({
          academicYearId: academicYear?.id,
          statusId: status?.id,
          searchText: searchText?.trim() || null,
          currentPage,
          pageSize: this.pagination.pageSize,
        });

        this.results = data.data || [];
        this.pagination = {
          pageSize: _DefaultPageSize,
          totalItemCount: data.totalItemCount,
          pageCount: data.pageCount,
          currentPage: data.currentPage,
          count: data.count,
        };
      } finally {
        this.isLoading = false;
      }
    },
    goToMinute(item) {
      this.$router.push({
        name: 'compu-minutes-editor-detail',
        params: { id: item.id },
      });
    },
    async handlePrintMinute(item) {
      if (
        item.statusId === compuMinuteStatus.Completada &&
        item.signedDocumentId
      ) {
        await documentsService.downloadFromServer(item.signedDocumentId);
        return;
      }

      const response = await compuMinuteService
        .downloadManuscriptTemplate(item.id)
        .catch(() => null);

      if (response?.data) {
        const blob = documentService.getBlobConvertion(response.data);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || 'Minuta de COMPU.pdf';
        link.click();
        URL.revokeObjectURL(url);
      }
    },
    async handleReturnToDraft(item) {
      if (!item?.id) return;

      const response = await compuMinuteService
        .revertToDraft(item.id)
        .catch((err) => {
          this.ShowToast(
            'Error',
            err?.response?.data?.message ||
              'No fue posible devolver la minuta a Borrador.',
            'error'
          );
        });

      if (!response?.data) return;

      this.ShowToast(
        'Proceso Exitoso',
        'La minuta ha sido devuelta a Borrador exitosamente.',
        'success'
      );

      await this.onSearchHandler(this.pagination.currentPage || 1);
    },
    async handleDiscardDraft(item) {
      if (!item?.id) return;

      const isConfirmed = await this.ShowCancelModal(
        'Descartar Borrador',
        'Esta acción es irreversible. El borrador será eliminado del sistema y no podrá recuperarse. ¿Desea continuar?',
        'Confirmar',
        'Cancelar'
      );

      if (!isConfirmed) return;

      const response = await compuMinuteService
        .discardDraft(item.id)
        .catch((err) => {
          this.ShowToast(
            'Error',
            err?.response?.data?.message ||
              'No fue posible descartar el borrador.',
            'error'
          );
        });

      if (!response?.data) return;

      this.ShowToast(
        'Proceso Exitoso',
        'El borrador ha sido descartado exitosamente.',
        'success'
      );

      await this.onSearchHandler(this.pagination.currentPage || 1);
    },
  },
};
</script>

<style scoped>
.compu-minutes-editor__results {
  margin-bottom: 24px;
}
</style>
